/*
 * Copyright (c) Nexign, JSC, 2019-2022.
 */
/**
 *
 *
 * @author Evgenij Tyan
 * @author Yakov Gushchin
 * @author Anton Panasenko
 * @author Aleksandr Perkhunov
 * @author Ilya Babanov
 * @version 1.21.0
 * @since 1.0.0
 */
import { useEffect, useRef } from 'react';
import type { FC } from 'react';

import { connect } from 'react-redux';
import type { Dispatch } from 'redux';
import { createStructuredSelector } from 'reselect';

import { useTableListApi } from '@/main/modules/table';
import type { IRootReducer } from '@/main/reducers/rootReducer';
import type { ISubscriberMobile } from '@/main/types/subscriberTypes';
import { Checkbox, LinkBlock, TextBlock } from '@components/common';
import { makeSelectTranslationCommon } from '@modules/internationalization/intSelectors';
import type { ICommonTranslation } from '@modules/internationalization/types';
import { Loader } from '@modules/loader/components/Loader';
import ButtonServiceUserDenied from '@modules/role/components/ButtonServiceUserDenied';

import {
    labelEditorCreate,
    labelEditorLoadList,
    labelEditorSearch,
    labelEditorSwitch,
    labelEditorUpdate,
} from '../labelEditorActions';
import type { ILabelEditorLoadListPayload } from '../labelEditorActions';
import {
    makeSelectorLabelEditorIsLoading,
    makeSelectorLabelEditorLabels,
    makeSelectorLabelEditorSearchValue,
} from '../labelEditorSelectors';
import { EEditableLabel } from '../labelEditorTypes';
import type { IEditableLabel } from '../labelEditorTypes';
import { LabelEditorList } from './LabelEditorList';
import LabelEditorSearch from './LabelEditorSearch';
import {
    LabelEditorCheckedAllContainer,
    LabelEditorContainerStyled,
    LabelEditorFooterStyled,
    LabelEditorHeaderStyled,
    LabelEditorModalStyled,
    LabelEditorTitleStyled,
} from './styled/LabelEditorStyled';

export interface ILabelEditorModalProps {
    type?: EEditableLabel;
    onCloseModal: () => void;
}

type TLabelEditorModal = ILabelEditorModalProps & IDispatchProps & IConnectedProps;

export const LabelEditorModal: FC<TLabelEditorModal> = (
    {
        loadLabelList,
        labels,
        switchLabel,
        updateLabel,
        onCloseModal,
        searchLabel,
        translation,
        createLabel,
        searchValue,
        isLoading,
    }) => {

    const { current: updatingLabels } = useRef<Map<number, IEditableLabel>>(new Map());
    const { current: subscribersLabelsIds } = useRef<Set<number>>(new Set);

    const { data: subscribers } = useTableListApi();

    subscribers.forEach((subscriber: ISubscriberMobile) => {
        const { checked, labels } = subscriber;
        if (checked && !!labels.length) {
            labels.forEach(label => {
                subscribersLabelsIds.add(label.id)
            });
        }
    });

    useEffect(() => {
        loadLabelList({ clearList: true })
    }, []);

    useEffect(() => {
        if (!!subscribersLabelsIds.size && labels.length) {
            labels.forEach(item => {
                if (subscribersLabelsIds.has(item.id)) {
                    updatingLabels.set(item.id, item);
                }
            })
        }
    }, [subscribersLabelsIds, labels, updatingLabels]);

    const selectAll = labels.every((item: IEditableLabel) => item.checked);

    const onApply = () => {
        const updating = Array.from(updatingLabels.values());

        updateLabel(updating);
        onCloseModal();
    };

    const checkedAllHandler = () => {
        searchLabel('');
        switchLabel(labels.map((item: IEditableLabel) => ({
            ...item,
            checked: !selectAll,
        })));

        labels.forEach(item => {
            const newItem = { ...item, checked: !selectAll };
            if (selectAll) {
                updatingLabels.set(newItem.id, newItem);
            } else {
                if (!subscribersLabelsIds.has(newItem.id)) {
                    updatingLabels.set(newItem.id, newItem);
                }
            }
        });
    };

    const handleSwitchLabel = (label: IEditableLabel) => {
        const newLabels = labels.map(item => item.id === label.id ? label : item);
        switchLabel(newLabels);
        updatingLabels.set(label.id, label);
    };

    return (
        <LabelEditorContainerStyled
            data-component={'modal-popup'}
            data-name={'label-editor-modal'}>
            <LabelEditorHeaderStyled isLabelsExist={!!labels.length}>
                <LabelEditorTitleStyled>
                    {translation.labelEditor.title}
                </LabelEditorTitleStyled>

                <LabelEditorSearch
                    createLabel={createLabel}
                    searchLabel={searchLabel}
                    searchValue={searchValue}
                    translation={translation.labelEditor}
                />

                {
                    !!labels.length &&
                    <LabelEditorCheckedAllContainer>
                        <Checkbox
                            checked={selectAll}
                            className="label-editor__all-checkbox"
                            dataName="LabelEditorCheckedAll"
                            onChange={checkedAllHandler}
                        />
                        <TextBlock>{translation.labelEditor.selectAll}</TextBlock>
                    </LabelEditorCheckedAllContainer>
                }
            </LabelEditorHeaderStyled>

            {
                !isLoading ?
                    <LabelEditorModalStyled>
                        <LabelEditorList
                            labels={labels}
                            searchValue={searchValue}
                            switchLabel={handleSwitchLabel}
                            translation={translation.labelEditor}
                        />
                    </LabelEditorModalStyled>
                    : <Loader showLoader={true}/>
            }

            <LabelEditorFooterStyled>
                <ButtonServiceUserDenied
                    dataName="labelEditorButtonApply"
                    kind="mediumNormal"
                    onClick={onApply}
                >
                    {translation.labelEditor.apply}
                </ButtonServiceUserDenied>
                <LinkBlock
                    className="label-editor__footer-link"
                    onClick={onCloseModal}
                >
                    {translation.labelEditor.close}
                </LinkBlock>
            </LabelEditorFooterStyled>
        </LabelEditorContainerStyled>
    );
};

interface IConnectedProps {
    isLoading: boolean;
    labels: IEditableLabel[];
    searchValue: string;
    translation: ICommonTranslation;
}

const mapStateToProps = createStructuredSelector<IRootReducer, IConnectedProps>({
    isLoading: makeSelectorLabelEditorIsLoading(),
    labels: makeSelectorLabelEditorLabels(),
    searchValue: makeSelectorLabelEditorSearchValue(),
    translation: makeSelectTranslationCommon(),
});

interface IDispatchProps {
    createLabel: (payload: string) => void;
    loadLabelList: (payload: ILabelEditorLoadListPayload) => void;
    searchLabel: (payload: string) => void;
    switchLabel: (payload: IEditableLabel[]) => void;
    updateLabel: (payload: IEditableLabel[]) => void;
}

const mapDispatchToProps = (dispatch: Dispatch): IDispatchProps => ({
    createLabel: (payload: string) => dispatch(labelEditorCreate(payload)),
    loadLabelList: (payload) => dispatch(labelEditorLoadList(payload)),
    searchLabel: (payload: string) => dispatch(labelEditorSearch(payload)),
    switchLabel: (payload: IEditableLabel[]) => dispatch(labelEditorSwitch(payload)),
    updateLabel: (payload: IEditableLabel[]) => dispatch(labelEditorUpdate(payload)),
});

export default connect(mapStateToProps, mapDispatchToProps)(LabelEditorModal);
